/**
 * Sugestões da base para um laboratório — usadas no "Adicionar da base" e no onboarding. Funções
 * puras: o servidor resolve perfis, químicas e equipamentos do laboratório e passa aqui.
 */
export type ContextoDoLab = {
  /** Perfis marcados no onboarding (CADCAM, CERAMICA…). Vazio em laboratório antigo. */
  perfis: ReadonlySet<string>;
  /** Químicas do que o laboratório já tem (materiais e insumos). */
  quimicas: ReadonlySet<string>;
  /** Ids da base dos equipamentos que o laboratório tem. */
  equipamentos: ReadonlySet<string>;
};

export type ItemSugerivel = {
  perfis: string[];
  /** Químicas do item: do serviço, do material, do equipamento ou do insumo (+ "usado com"). */
  quimicas: string[];
  /** Só insumo: equipamentos da base que gastam o item. */
  equipamentos?: string[];
};

const algum = <T>(lista: T[], conjunto: ReadonlySet<T>) => lista.some((x) => conjunto.has(x));

/** O perfil casa: item de todo laboratório, ou um perfil em comum. */
export const casaPerfil = (item: Pick<ItemSugerivel, "perfis">, ctx: Pick<ContextoDoLab, "perfis">) =>
  item.perfis.includes("GERAL") || algum(item.perfis, ctx.perfis);

/** Serviço e material: pelo perfil. */
export const servicoOuMaterialSugerido = (item: ItemSugerivel, ctx: ContextoDoLab) => casaPerfil(item, ctx);

/** Equipamento: pelo perfil, ou por trabalhar com uma química que o laboratório já usa. */
export const equipamentoSugerido = (item: ItemSugerivel, ctx: ContextoDoLab) =>
  casaPerfil(item, ctx) || algum(item.quimicas, ctx.quimicas);

/**
 * Insumo: gasto por um equipamento que o laboratório tem, ou da química de um material dele.
 * Insumo "de todo laboratório" (gesso, cera) entra sempre.
 */
export const insumoSugerido = (item: ItemSugerivel, ctx: ContextoDoLab) =>
  item.perfis.includes("GERAL") || algum(item.equipamentos ?? [], ctx.equipamentos) || algum(item.quimicas, ctx.quimicas);

/**
 * Onboarding: os perfis do laboratório saem dos serviços escolhidos (o que alimenta as sugestões
 * depois). Serviço da base costuma ter mais de um perfil (provisório impresso é CAD/CAM e 3D), então
 * um serviço avulso não basta: o perfil vale quando o laboratório pegou ao menos um terço dos dele.
 */
export function perfisDosServicos(servicos: { id: string; perfis: string[] }[], escolhidos: ReadonlySet<string>) {
  const total = new Map<string, number>();
  const marcados = new Map<string, number>();
  for (const s of servicos) {
    for (const p of s.perfis) {
      if (p === "GERAL") continue;
      total.set(p, (total.get(p) ?? 0) + 1);
      if (escolhidos.has(s.id)) marcados.set(p, (marcados.get(p) ?? 0) + 1);
    }
  }
  return [...total].filter(([p, n]) => (marcados.get(p) ?? 0) * 3 >= n && (marcados.get(p) ?? 0) > 0).map(([p]) => p).sort();
}

/** Chave de item criado no onboarding (serviço ou material fora da base), no lugar do id da base. */
export const chaveDoProprio = (nome: string) => `proprio:${nome}`;

/**
 * Onboarding: os materiais de cada serviço escolhido — o que a pessoa configurou no serviço, ou o
 * padrão da base (pares explícitos). Serviço criado pela pessoa começa sem material.
 */
export function materiaisPorServico(
  servicos: { id: string; materiais: string[] }[],
  escolhidos: string[],
  proprios: string[],
  configurados: Record<string, string[]>,
) {
  const daBase = new Map(servicos.map((s) => [s.id, s.materiais]));
  const mapa: Record<string, string[]> = {};
  for (const id of escolhidos) mapa[id] = configurados[id] ?? daBase.get(id) ?? [];
  for (const nome of proprios) mapa[chaveDoProprio(nome)] = configurados[chaveDoProprio(nome)] ?? [];
  return mapa;
}
